import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Database, Fish, Globe, Landmark, MapPinned, Percent, Scale, Settings2, Ship, Users, Wallet, Anchor } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { FormField, PageHeader, Select } from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { cx } from '@/lib/cx';
import { CharterFeesEditor, FisheryZonesEditor, OperatorSettingsEditor, PortsEditor, SurveyDayLimitsEditor, WagesEditor } from './admin/YearMasters';
import { FishSpeciesEditor, OperatorsEditor, RatePeriodsEditor, SeaAreaGridEditor } from './admin/OtherMasters';
import './masters.css';

type Kind = {
  key: string;
  title: string;
  code: string;
  desc: string;
  icon: LucideIcon;
  yearly: boolean;
};

/** マスタ種別（02_基本設計書 S-92 の kind）。code は海図の区画番号に見立てた装飾 */
export const MASTER_KINDS: Kind[] = [
  { key: 'operators', title: '事業実施者', code: 'M-01', desc: '漁協等の名称・正式名称・漁獲CSV取込形式', icon: Users, yearly: false },
  { key: 'operator-settings', title: '事業実施者 年度別設定', code: 'M-02', desc: '消費税の有無・インボイス・税の端数処理・表示順', icon: Settings2, yearly: true },
  { key: 'charter-fees', title: '用船料', code: 'M-03', desc: 'トン数帯ごとの用船料（円/日）', icon: Ship, yearly: true },
  { key: 'wages', title: '賃金', code: 'M-04', desc: '作業時間帯ごとの賃金（円/人）', icon: Wallet, yearly: true },
  { key: 'fishery-zones', title: '農林漁区番号表', code: 'M-05', desc: '緯度経度 → 農林漁区番号・海域区分（S/A/B）', icon: MapPinned, yearly: true },
  { key: 'survey-day-limits', title: '年間限度調査日数', code: 'M-06', desc: '海域別・合計の上限日数とプラス日数', icon: Scale, yearly: true },
  { key: 'ports', title: '港名', code: 'M-07', desc: '事業実施者ごとの港名', icon: Anchor, yearly: true },
  { key: 'rate-periods', title: '期間別税率', code: 'M-08', desc: '消費税・インボイス控除割合・軽油引取税・潤滑油係数', icon: Percent, yearly: false },
  { key: 'fish-species', title: '魚種・類型', code: 'M-09', desc: '漁獲CSV・漁場情報で使う魚種と類型', icon: Fish, yearly: false },
  { key: 'sea-area-grid', title: '海域図セル', code: 'M-10', desc: '海域図（18行×30列）の農林漁区番号', icon: Globe, yearly: false },
];

/** S-92 マスタ管理 トップ（種別の一覧） */
export function MasterIndexPage() {
  const { yearPath } = useFiscalYear();
  return (
    <>
      <PageHeader eyebrow="システム管理" title="マスタ管理" description="業務画面の選択肢・計算に使うマスタを管理します。年度別のマスタは、年次更新処理で前年度から複製されます。" />
      <div className="bs-master-tabs">
        {MASTER_KINDS.map((k) => (
          <Link key={k.key} to={yearPath(`admin/masters/${k.key}`)} className="bs-master-card">
            <span className="bs-master-card__code">{k.code}{k.yearly ? ' ／ 年度別' : ''}</span>
            <span className="bs-master-card__title"><k.icon size={16} aria-hidden style={{ verticalAlign: -2, marginRight: 6 }} />{k.title}</span>
            <span className="bs-master-card__desc">{k.desc}</span>
          </Link>
        ))}
      </div>
      <p className="bs-readonly-note"><Database size={14} aria-hidden style={{ verticalAlign: -2, marginRight: 6 }} /><Landmark size={14} aria-hidden style={{ verticalAlign: -2, marginRight: 6 }} />更新は基金のみ可能です。漁協・水産庁・沖縄県の画面では、必要なマスタ（選択肢・税率など）が各業務画面を通じて自動で参照されます。</p>
    </>
  );
}

/** S-92 マスタ管理 種別ごとの編集（/admin/masters/:kind?year=） */
export function MasterKindPage() {
  const { kind } = useParams();
  const { year: routeYear, years, yearPath } = useFiscalYear();
  const [params, setParams] = useSearchParams();
  const def = MASTER_KINDS.find((k) => k.key === kind);
  if (!def) return <Navigate to={yearPath('admin/masters')} replace />;
  const qYear = Number(params.get('year'));
  const year = def.yearly && years.some((y) => y.year === qYear) ? qYear : routeYear;

  return (
    <>
      {def.key === 'operators' ? null : <PageHeader eyebrow="システム管理 ／ マスタ管理" title={def.title} description={def.desc} />}
      <nav className="bs-master-nav" aria-label="マスタ種別">
        <Link to={yearPath('admin/masters')}>一覧</Link>
        {MASTER_KINDS.map((k) => (
          <Link key={k.key} to={yearPath(`admin/masters/${k.key}`)} className={cx(k.key === def.key && 'is-active')} aria-current={k.key === def.key ? 'page' : undefined}>
            {k.title}
          </Link>
        ))}
      </nav>
      {def.yearly && (
        <div className="bs-master-toolbar">
          <FormField label="対象年度" layout="horizontal">
            <Select
              width="md"
              value={year}
              onChange={(e) => {
                const next = new URLSearchParams(params);
                next.set('year', e.target.value);
                setParams(next, { replace: true });
              }}
              options={years.map((y) => ({ value: y.year, label: `${y.label}（${y.year}年度）` }))}
            />
          </FormField>
        </div>
      )}
      <KindEditor kind={def.key} year={year} />
    </>
  );
}

function KindEditor({ kind, year }: { kind: string; year: number }) {
  switch (kind) {
    case 'operators':
      return <OperatorsEditor />;
    case 'operator-settings':
      return <OperatorSettingsEditor key={year} year={year} />;
    case 'charter-fees':
      return <CharterFeesEditor key={year} year={year} />;
    case 'wages':
      return <WagesEditor key={year} year={year} />;
    case 'fishery-zones':
      return <FisheryZonesEditor key={year} year={year} />;
    case 'survey-day-limits':
      return <SurveyDayLimitsEditor key={year} year={year} />;
    case 'ports':
      return <PortsEditor key={year} year={year} />;
    case 'rate-periods':
      return <RatePeriodsEditor />;
    case 'fish-species':
      return <FishSpeciesEditor />;
    case 'sea-area-grid':
      return <SeaAreaGridEditor />;
    default:
      return null;
  }
}
